import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mapApiProfileToProfile, mapProfileFormToDto } from '../../domain/mappers';
import type { ProfileInfoForm } from '../../domain/schemas';
import { fetchProfile, updateProfile, updateProfileImage } from '../service';
import { PROFILE_QUERY_KEY, PROFILE_IMAGE_MUTATION_KEY, USER_INFO_QUERY_KEY } from '../constants';
import { TWENTY_FOUR_HOURS_IN_MS } from '@/shared/constants';

export const useGetProfile = () => {
	return useQuery({
		queryKey: [PROFILE_QUERY_KEY],
		queryFn: async () => mapApiProfileToProfile(await fetchProfile()),
		staleTime: TWENTY_FOUR_HOURS_IN_MS,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
	});
};

export type UpdateProfileCallbacks = {
	onUpdated?: (form: ProfileInfoForm) => void;
	onFailed?: () => void;
};

export const useUpdateProfile = (callbacks?: UpdateProfileCallbacks) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (form: ProfileInfoForm) => updateProfile(mapProfileFormToDto(form)),
		onSuccess: (_data, form) => {
			queryClient.invalidateQueries({ queryKey: [USER_INFO_QUERY_KEY] });
			queryClient.invalidateQueries({ queryKey: [PROFILE_QUERY_KEY] });
			callbacks?.onUpdated?.(form);
		},
		onError: () => callbacks?.onFailed?.(),
	});
};

export const useUpdateProfileImage = () => {
	return useMutation({
		mutationKey: PROFILE_IMAGE_MUTATION_KEY,
		mutationFn: (file: File) => updateProfileImage(file),
	});
};
